import { useState } from 'react';
import {
  modifierLibelleFormacode,
  analyserRemappage,
  appliquerRemappage,
  type PlanRemappage,
} from '@/api/activites';
import { ApiError } from '@/api/client';
import { ErrorMessage } from '@/components/ErrorMessage';
import { confirmationRemappage, resumeRemappage } from '@/utils/correctionFormacode';

interface Props {
  codeFormacode: string;
  intitule: string;
  /** Après une correction de libellé : la page se recharge. */
  onLibelleModifie: () => void;
  /** Après un remappage appliqué : l'ancien code n'existe plus, la page doit en sortir. */
  onRemappe: (cible: string) => void;
}

/**
 * Corriger un formacode, en deux opérations distinctes :
 *   - le libellé canonique (sans effet sur les passerelles) ;
 *   - le remplacement d'un code erroné par un code EXISTANT : analyse à blanc d'abord,
 *     puis application confirmée en saisissant `ANCIEN>CIBLE`. Un conflit bloque.
 */
export function CorrectionFormacode({ codeFormacode, intitule, onLibelleModifie, onRemappe }: Props) {
  const [libelle, setLibelle] = useState(intitule);
  const [cible, setCible] = useState('');
  const [plan, setPlan] = useState<PlanRemappage | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function enregistrerLibelle() {
    setEnCours(true);
    setErreur(null);
    try {
      await modifierLibelleFormacode(codeFormacode, libelle);
      onLibelleModifie();
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Libellé non enregistré');
    } finally {
      setEnCours(false);
    }
  }

  async function analyser() {
    setEnCours(true);
    setErreur(null);
    setPlan(null);
    setConfirmation('');
    try {
      setPlan(await analyserRemappage(codeFormacode, cible.trim()));
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Analyse impossible');
    } finally {
      setEnCours(false);
    }
  }

  async function appliquer() {
    if (!plan) return;
    setEnCours(true);
    setErreur(null);
    try {
      await appliquerRemappage(codeFormacode, plan.nouveau, confirmation);
      onRemappe(plan.nouveau);
    } catch (err) {
      setErreur(err instanceof ApiError ? err.message : 'Remappage impossible');
    } finally {
      setEnCours(false);
    }
  }

  const attendu = plan ? confirmationRemappage(codeFormacode, plan.nouveau) : '';

  return (
    <section className="fiche__section">
      <h2>Corriger ce domaine de connaissance</h2>
      {erreur && <ErrorMessage message={erreur} />}

      <div className="passerelles-champ">
        <label htmlFor="correction-libelle">Libellé</label>
        <div className="liste-edition__ligne">
          <input
            id="correction-libelle"
            type="text"
            className="edition__texte"
            maxLength={255}
            value={libelle}
            disabled={enCours}
            onChange={(e) => setLibelle(e.target.value)}
          />
          <button
            type="button"
            className="bouton--secondaire"
            onClick={enregistrerLibelle}
            disabled={enCours || !libelle.trim() || libelle.trim() === intitule}
          >
            Enregistrer le libellé
          </button>
        </div>
        <p className="detail">
          Sans effet sur les passerelles. Le libellé saisi lors de la collecte sur chaque fiche
          est conservé, mais les écrans et exports affichent celui-ci en priorité.
        </p>
      </div>

      <div className="passerelles-champ">
        <label htmlFor="correction-cible">Remplacer le code {codeFormacode} par un code existant</label>
        <div className="liste-edition__ligne">
          <input
            id="correction-cible"
            type="text"
            className="edition__texte"
            maxLength={10}
            placeholder="Code formacode cible"
            value={cible}
            disabled={enCours}
            onChange={(e) => {
              setCible(e.target.value);
              setPlan(null);
            }}
          />
          <button
            type="button"
            className="bouton--secondaire"
            onClick={analyser}
            disabled={enCours || !cible.trim()}
          >
            Analyser
          </button>
        </div>
        <p className="detail">
          Les fiches qui citent {codeFormacode} citeront le code cible, puis {codeFormacode} sera
          supprimé. L’analyse n’écrit rien.
        </p>
      </div>

      {plan && (
        <div className={plan.statut === 'applicable' ? 'bandeau-alerte' : 'erreur'} role="status">
          <p>{resumeRemappage(plan)}</p>
          {plan.statut === 'applicable' && (
            <div className="liste-edition__ligne">
              <input
                type="text"
                className="edition__texte"
                aria-label={`Confirmation : saisir ${attendu}`}
                placeholder={`Saisir ${attendu} pour confirmer`}
                value={confirmation}
                disabled={enCours}
                onChange={(e) => setConfirmation(e.target.value)}
              />
              <button
                type="button"
                className="bouton--export"
                onClick={appliquer}
                disabled={enCours || confirmation !== attendu}
              >
                Remplacer définitivement
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
